import { Component, inject, ViewChild } from '@angular/core';
import { BaseViewComponent } from '../base-view.component';
import { StateChangedModel } from '../../common/services/state.service';
import { SecureDataService } from '../../common/services/securedata.sevice';
import { LanguageStateService } from '../../common/services/language-state.service';
import { take } from 'rxjs';
import { AccountLookupComponent, AccountValidatedEvent } from './account-lookup/account-lookup.component';
import { OtpDeliveryComponent, PinSentEvent } from './otp-delivery/otp-delivery.component';
import { OtpVerificationComponent } from './otp-verification/otp-verification.component';

export type StepStatus = 'pending' | 'active' | 'completed';

@Component({
    selector: 'app-wireless-account-authentication',
    templateUrl: './wireless-account-authentication.component.html',
    styleUrl: './wireless-account-authentication.component.scss',
    standalone: false
})
export class WirelessAccountAuthenticationComponent extends BaseViewComponent {
  #secureDataService: SecureDataService = inject(SecureDataService)
  languageService: LanguageStateService = inject(LanguageStateService)

  @ViewChild(AccountLookupComponent) accountLookup: AccountLookupComponent
  @ViewChild(OtpDeliveryComponent) otpDelivery: OtpDeliveryComponent
  @ViewChild(OtpVerificationComponent) otpVerification: OtpVerificationComponent

  showCancelDialog = false
  showClearSelectionDialog = false
  showValidationSuccessModal = false

  connectionError = false

  isAccountValidated = false
  accountNumber: number
  authUserList: string[] = []
  pinDeliveryOptions: { email?: string; ctnOptions: string[] } = { ctnOptions: [] }

  selectedPinDeliveryMethod: string | null = null
  showPinFields = false
  isPinExpired = false

  isPinValidated = false
  private lastAuthResponse: any

  wirelessSetupCustomerOrderPayload: any

  constructor() {
    super()
  }

  protected override refreshView(): void {
  }

  protected override onSecureDataChanged(val: StateChangedModel): void {
    if (val.Type == 'WirelessAccountAuthentication' && val.Data != undefined) {
      const data = val.Data as any
      this.wirelessSetupCustomerOrderPayload = data?.wirelessSetupCustomerOrderPayload || {}
    }
  }

  step1Status(): StepStatus {
    return this.isAccountValidated ? 'completed' : 'active'
  }

  step2Status(): StepStatus {
    if (!this.isAccountValidated) return 'pending'
    return this.showPinFields ? 'completed' : 'active'
  }

  step3Status(): StepStatus {
    if (this.isPinValidated) return 'completed'
    return this.showPinFields ? 'active' : 'pending'
  }

  // ===================== Step 1 event handlers =====================

  onAccountValidated(event: AccountValidatedEvent) {
    this.accountNumber = event.accountNumber
    this.authUserList = event.authUserList
    this.pinDeliveryOptions = event.pinDeliveryOptions
    this.isAccountValidated = true
  }

  onClearSelectionRequested() {
    this.showClearSelectionDialog = true
  }

  // ===================== Step 2 event handlers =====================

  onPinSent(event: PinSentEvent) {
    this.selectedPinDeliveryMethod = event.selectedMethod
    this.showPinFields = true
    this.isPinExpired = false
  }

  onPinExpired() {
    this.isPinExpired = true
  }

  // ===================== Step 3 event handlers =====================

  onPinValidated(response: any) {
    this.lastAuthResponse = response
    this.showValidationSuccessModal = true
  }

  onValidationModalVisibilityChange(visible: boolean) {
    this.showValidationSuccessModal = visible
    if (!visible) {
      this.isPinValidated = true
      this.otpVerification.lockAfterValidation()
      this.otpDelivery.markPinValidated()
    }
  }

  // ===================== Shared handlers =====================

  onConnectionError() {
    this.connectionError = true
  }

  submitClicked() {
    if (this.isPinValidated && this.lastAuthResponse) {
      const data = { authUserList: this.authUserList, response: this.lastAuthResponse }
      this.#secureDataService.submitCustomerData(data, 'WirelessAccountAuthentication')
        .subscribe({
          next: () => {
            this.navigateWaiting()
          },
          error: (err) => {
            this.notificationService.showError(err)
          }
        })
    }
  }

  onCancelClick() {
    this.showCancelDialog = true
  }

  onCancel(data: unknown) {
    this.showCancelDialog = false
    if (data && (data as { data: { success: boolean } }).data?.success) {
      this.#secureDataService.cancel('WirelessAccountAuthentication-Cancel').pipe(take(1)).subscribe({
        next: () => {
          this.navigateWaiting(true)
        },
        error: (err) => this.onCancelError('WirelessAccountAuthentication-Cancel', err)
      })
    }
  }

  onClearSelection(data: unknown) {
    this.showClearSelectionDialog = false
    if (data && (data as { data: { success: boolean } }).data?.success) {
      this.resetAll()
    }
  }

  private resetAll() {
    this.accountLookup.reset()
    this.otpDelivery.reset()
    this.otpVerification.reset()

    this.isAccountValidated = false
    this.accountNumber = undefined as unknown as number
    this.authUserList = []
    this.pinDeliveryOptions = { ctnOptions: [] }

    this.selectedPinDeliveryMethod = null
    this.showPinFields = false
    this.isPinExpired = false

    this.isPinValidated = false
    this.lastAuthResponse = undefined

    this.connectionError = false
  }
}